import { Directive, ElementRef, Input, OnInit, SecurityContext, ViewContainerRef, Renderer2, HostListener } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import * as _ from 'lodash';
import { environment } from 'src/environments/environment';
import { CustomIdTags } from './custom-id-tags';

/**
 * How to use this directive.
 * 1. Import the DirectivesModule to the module of the component
 * 2. Use the directive to the element you want to add id
 * 3. The directive accepts 3 strings in an array as input. 1st is custom text, 2nd is custom component name, 3rd is custom element tag name.
 * Custom Text is not required but is a must.
 * 
 * eg. escCustomId="password,componentName,txt"
 * eg. escCustomId="password,componentName" - uses auto generation of tag name only 
 * eg. escCustomId="password" - uses auto generation of element tag name and component name
 * 
 * result: id_componentName_txt_password
 * 
 * How to enable id helper. (show id on tooltip on hover)
 * 1. Simply add in "id-helper" key with value 1 on your browsers Local Storage then reload the page. OR run this on the developer console: 
 * localStorage.setItem('id-helper',1)
 * 
 * Reverted to original. I came to realize that you wouldnt just update the Angular version right?
 * So unless you dont update the Angualar version, extraction of component name will work.
 * That feature is too good to lose :/ 
 * 
 * NOTE: 
 * 1. On cases where the extracted component name is not as expected. Just override the component name generation by providing the component name in the directive OR by setting escComponentName on a parent component.
 * 2. On cases where you are using this for DROP DOWN where the items are reloaded every interaction. Make sure to add the directive to the formfield component, Sample: mat-automcomplete, where there is an 'opened' event.
 * List of formfield that has 'opened' event: mat-dialog, mat-menu, mat-datepicker, mat-autocomplete
 * 3. On cases where you are showing a loading item on a list, add escPreserveIndex = true on the loading item so it will not reset the index for the list.
 */

interface IIndexedId {
  id_name: string,
  current_index: number
}

@Directive({
  selector: '[escCustomId]'
})
export class CustomIdDirective implements OnInit{

  @Input() escCustomId = Array<string>();
  @Input() escCustomIdIndex: boolean = false;
  @Input() escComponentName: string = null;
  @Input() escPreserveIndex: boolean = false;

  private static indexedId: IIndexedId[] = [];
  private static assignedComponentName: string;
  private showIdHelper = localStorage.getItem('id-helper');

  constructor(
    private el:ElementRef,
    private sanitizer: DomSanitizer,
    private _view: ViewContainerRef,
    private renderer: Renderer2) { }

  ngOnInit(): void {

    let sanitizedEntry = this.sanitizer.sanitize(SecurityContext.HTML, this.escCustomId)
    let customIdProperties = sanitizedEntry.split(',');
    
    if (this.escComponentName) {
      CustomIdDirective.assignedComponentName = this.escComponentName;
    }

    if (customIdProperties.length < 1) {
      throw new Error("ESC CUSTOM ID: custom text is required sample: escCustomId(logo)")
    }
    
    this.el.nativeElement.id = this.generateID(this.el, customIdProperties[0], customIdProperties[2], customIdProperties[1]);

    if (this.showIdHelper === "1") {
      this.renderer.setAttribute(this.el.nativeElement,"title", this.el.nativeElement.id)
    }    
    
  }

  generateID(element:ElementRef, customText:string, customTagName:string, componentName:string){
    let generatedComponentName = '';
    try {
      if(!componentName){
        generatedComponentName = this.getComponentName(this._view, customText)
      } else {
        generatedComponentName = componentName
      }
    } catch (e) {
      console.log(e)
    }

    let text = customText.trim();
    let tagName = customTagName ? customTagName.trim() : null;

    let tagNameAlias = tagName ? tagName : this.getTagNameAlias(element.nativeElement.tagName.toLowerCase())
    let id = "id_" + generatedComponentName + "_" + tagNameAlias + "_" + text;

    if (this.escCustomIdIndex) {
      id = this.getIndexedId(id);
    } else {
      // reset: if the next usage does not have escCustomIdIndex
      if (!this.escPreserveIndex) {
        CustomIdDirective.indexedId = [];
      }
    }

    return id
  }

  getTagNameAlias(tagName: string) {
    let label = _.find(CustomIdTags.CUSTOM_ID_TAGS, {tagName:tagName})
    if (!label) {
      // throw new Error("ESC CUSTOM ID: No assigned alias for - " + tagName)
      if (!environment.production && this.showIdHelper === "1") {
        console.info("ESC CUSTOM ID: No assigned alias for - " + tagName)
      };
    }
    return label ? label.alias : tagName;

  }

  /**
   * generates id with indexing
   * 
   * NOTE: when using this on tables or other embedded views, you might not get the proper component name.
   * For now, just override the component name
   * 
   * @param initialId generated id without index
   * @returns id with index
   */
  getIndexedId(initialId: string) {
  
    let exists = _.findIndex(CustomIdDirective.indexedId, { id_name: initialId });

    if (exists === -1) {
      let newIndexedId: IIndexedId = {
        id_name: initialId,
        current_index: 0
      }
      CustomIdDirective.indexedId.push(newIndexedId);
      return initialId + "_" + 0;
    } else {
      CustomIdDirective.indexedId[exists].current_index++;
      return initialId + "_" + CustomIdDirective.indexedId[exists].current_index;
    }
    
  }

  /**
   * This function is brittle and might be too dependent on the current structure
   * of the current Angular Version which is Angular 14.2.3.
   * 
   * Please check if the path still exist when upgrading Angular version.
   * @param vc 
   */
  getComponentName(vc: ViewContainerRef, errorIdentifier: string) {
    
    // If component name is provided
    if (CustomIdDirective.assignedComponentName) {
      return CustomIdDirective.assignedComponentName;
    }
    
    // Else, will try to generate component name automaticaly
    // Path for Embedded Views
    if (vc.injector['_lView'][0] === null || !vc.injector['_lView'][0].tagName.toLowerCase().includes('esc')) {
      if (!environment.production && this.showIdHelper === "1") {
        console.warn("ESC CUSTOM ID: Embedded View detected. Please check generated component_name for - " + errorIdentifier);
      };
      return vc.injector['_lView'][3][3][0].tagName.toLowerCase().replaceAll('-','_')
    
    // Path for Component Views
    } else if (vc.injector['_lView'][0]){
      return vc.injector['_lView'][0].tagName.toLowerCase().replaceAll('-','_')

    } else {
      throw new Error("ESC CUSTOM ID: Error getting component name. Have you updated the Angular version? Please provide a component name instead.")
    }
  }

  resetIndex() {
    CustomIdDirective.indexedId = []
  }

  // listen to every opened event where this directive is attached.
  // since this event is attached to every element. Please give it one small task only.
  @HostListener('opened', ['$event'])
  onClick(event: any): void{
    this.resetIndex();
  }

}
